import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Slide,
  Typography,
  TextField,
  Grid,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert
} from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import ScienceIcon from '@mui/icons-material/Science';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import FavoriteIcon from '@mui/icons-material/Favorite';
import PsychologyIcon from '@mui/icons-material/Psychology';
import OpacityIcon from '@mui/icons-material/Opacity';
import AssignmentIcon from '@mui/icons-material/Assignment';
import RefreshIcon from '@mui/icons-material/Refresh';

// Componente de transición
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Componente para mostrar un campo con label (igual que CargarFlujoDialog)
const InfoField = ({ label, value, color = "rgba(23, 66, 136, 0.85)" }) => (
  <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, fontSize: 12 }}>
    <Typography component="span" sx={{ fontWeight: 600, color: "#64748b", fontSize: 11 }}>
      {label}:
    </Typography>
    <Typography component="span" sx={{ color, fontWeight: 500, fontSize: 12 }}>
      {value || "—"}
    </Typography>
  </Box>
);

// Componente de resultado
const ResultadoBox = ({ titulo, valor, unidad, interpretacion, color = '#1e3a5f' }) => (
  <Box sx={{
    p: 2,
    mt: 2,
    bgcolor: '#fff',
    borderRadius: 2,
    border: '1px solid #e2e8f0',
    boxShadow: '0 2px 8px rgba(99, 102, 241, 0.1)'
  }}>
    <Typography sx={{ fontSize: 11, color: '#64748b', mb: 0.5, fontWeight: 600 }}>{titulo}</Typography>
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
      <Typography sx={{ fontSize: 28, fontWeight: 700, color }}>{valor}</Typography>
      {unidad && <Typography sx={{ fontSize: 13, color: '#64748b' }}>{unidad}</Typography>}
    </Box>
    {interpretacion && (
      <Chip
        label={interpretacion}
        size="small"
        sx={{ mt: 1, bgcolor: color + '20', color: color, fontWeight: 600, fontSize: 11 }}
      />
    )}
  </Box>
);

// Lista de calculadoras
const CALCULADORAS = [
  { id: 'imc', name: 'IMC', icon: <FitnessCenterIcon sx={{ fontSize: 16 }} />, color: '#10b981' },
  { id: 'creatinina', name: 'Clearance', icon: <ScienceIcon sx={{ fontSize: 16 }} />, color: '#3b82f6' },
  { id: 'superficie', name: 'Superficie', icon: <AssignmentIcon sx={{ fontSize: 16 }} />, color: '#8b5cf6' },
  { id: 'pediatrica', name: 'Pediátrica', icon: <ChildCareIcon sx={{ fontSize: 16 }} />, color: '#f59e0b' },
  { id: 'fpp', name: 'FPP', icon: <FavoriteIcon sx={{ fontSize: 16 }} />, color: '#ec4899' },
  { id: 'glasgow', name: 'Glasgow', icon: <PsychologyIcon sx={{ fontSize: 16 }} />, color: '#6366f1' },
  { id: 'apgar', name: 'APGAR', icon: <ChildCareIcon sx={{ fontSize: 16 }} />, color: '#14b8a6' },
  { id: 'wells', name: 'Wells', icon: <OpacityIcon sx={{ fontSize: 16 }} />, color: '#ef4444' },
  { id: 'riesgocv', name: 'Riesgo CV', icon: <FavoriteIcon sx={{ fontSize: 16 }} />, color: '#dc2626' }
];

// ===== CALCULADORA IMC =====
const CalculadoraIMC = () => {
  const [peso, setPeso] = useState('');
  const [altura, setAltura] = useState('');
  const [resultado, setResultado] = useState(null);

  const calcular = () => {
    const p = parseFloat(peso);
    const h = parseFloat(altura) / 100;
    if (p > 0 && h > 0) {
      const imc = p / (h * h);
      let interpretacion = '', color = '#10b981';
      if (imc < 18.5) { interpretacion = 'Bajo peso'; color = '#f59e0b'; }
      else if (imc < 25) { interpretacion = 'Peso normal'; color = '#10b981'; }
      else if (imc < 30) { interpretacion = 'Sobrepeso'; color = '#f59e0b'; }
      else if (imc < 35) { interpretacion = 'Obesidad grado I'; color = '#ef4444'; }
      else if (imc < 40) { interpretacion = 'Obesidad grado II'; color = '#dc2626'; }
      else { interpretacion = 'Obesidad grado III'; color = '#991b1b'; }
      setResultado({ valor: imc.toFixed(1), interpretacion, color });
    }
  };

  const limpiar = () => { setPeso(''); setAltura(''); setResultado(null); };

  return (
    <Box>
      <Typography sx={{ fontSize: 12, color: '#64748b', mb: 2 }}>
        Calcula el Índice de Masa Corporal para evaluar el estado nutricional.
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField fullWidth size="small" label="Peso (kg)" type="number" value={peso} onChange={(e) => setPeso(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }} />
        </Grid>
        <Grid item xs={6}>
          <TextField fullWidth size="small" label="Altura (cm)" type="number" value={altura} onChange={(e) => setAltura(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }} />
        </Grid>
      </Grid>
      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
        <Button variant="contained" onClick={calcular} startIcon={<CalculateIcon />} sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none', background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)' }}>Calcular</Button>
        <Button variant="outlined" onClick={limpiar} startIcon={<RefreshIcon />} sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none', borderColor: '#e2e8f0', color: '#64748b' }}>Limpiar</Button>
      </Box>
      {resultado && <ResultadoBox titulo="Índice de Masa Corporal" valor={resultado.valor} unidad="kg/m²" interpretacion={resultado.interpretacion} color={resultado.color} />}
    </Box>
  );
};

// ===== CLEARANCE DE CREATININA =====
const CalculadoraClearance = () => {
  const [edad, setEdad] = useState('');
  const [peso, setPeso] = useState('');
  const [creatinina, setCreatinina] = useState('');
  const [sexo, setSexo] = useState('masculino');
  const [resultado, setResultado] = useState(null);

  const calcular = () => {
    const e = parseFloat(edad), p = parseFloat(peso), cr = parseFloat(creatinina);
    if (e > 0 && p > 0 && cr > 0) {
      let clearance = ((140 - e) * p) / (72 * cr);
      if (sexo === 'femenino') clearance *= 0.85;
      let interpretacion = '', color = '#10b981';
      if (clearance >= 90) { interpretacion = 'Normal'; color = '#10b981'; }
      else if (clearance >= 60) { interpretacion = 'ERC Estadio 2'; color = '#84cc16'; }
      else if (clearance >= 45) { interpretacion = 'ERC Estadio 3a'; color = '#f59e0b'; }
      else if (clearance >= 30) { interpretacion = 'ERC Estadio 3b'; color = '#f97316'; }
      else if (clearance >= 15) { interpretacion = 'ERC Estadio 4'; color = '#ef4444'; }
      else { interpretacion = 'ERC Estadio 5'; color = '#991b1b'; }
      setResultado({ valor: clearance.toFixed(1), interpretacion, color });
    }
  };

  const limpiar = () => { setEdad(''); setPeso(''); setCreatinina(''); setResultado(null); };

  return (
    <Box>
      <Typography sx={{ fontSize: 12, color: '#64748b', mb: 2 }}>Fórmula de Cockcroft-Gault para estimar la función renal.</Typography>
      <Grid container spacing={2}>
        <Grid item xs={6}><TextField fullWidth size="small" label="Edad (años)" type="number" value={edad} onChange={(e) => setEdad(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }} /></Grid>
        <Grid item xs={6}><TextField fullWidth size="small" label="Peso (kg)" type="number" value={peso} onChange={(e) => setPeso(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }} /></Grid>
        <Grid item xs={6}><TextField fullWidth size="small" label="Creatinina (mg/dL)" type="number" value={creatinina} onChange={(e) => setCreatinina(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }} /></Grid>
        <Grid item xs={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Sexo</InputLabel>
            <Select value={sexo} label="Sexo" onChange={(e) => setSexo(e.target.value)} sx={{ borderRadius: 2, bgcolor: '#fff' }}>
              <MenuItem value="masculino">Masculino</MenuItem>
              <MenuItem value="femenino">Femenino</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
        <Button variant="contained" onClick={calcular} startIcon={<CalculateIcon />} sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none', background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)' }}>Calcular</Button>
        <Button variant="outlined" onClick={limpiar} startIcon={<RefreshIcon />} sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none', borderColor: '#e2e8f0', color: '#64748b' }}>Limpiar</Button>
      </Box>
      {resultado && <ResultadoBox titulo="Clearance de Creatinina (CrCl)" valor={resultado.valor} unidad="mL/min" interpretacion={resultado.interpretacion} color={resultado.color} />}
    </Box>
  );
};

// ===== SUPERFICIE CORPORAL =====
const CalculadoraSuperficie = () => {
  const [peso, setPeso] = useState('');
  const [altura, setAltura] = useState('');
  const [resultado, setResultado] = useState(null);

  const calcular = () => {
    const p = parseFloat(peso), h = parseFloat(altura);
    if (p > 0 && h > 0) {
      const sc = Math.sqrt((p * h) / 3600);
      setResultado({ valor: sc.toFixed(2) });
    }
  };

  const limpiar = () => { setPeso(''); setAltura(''); setResultado(null); };

  return (
    <Box>
      <Typography sx={{ fontSize: 12, color: '#64748b', mb: 2 }}>Fórmula de Mosteller. Útil para dosificación de quimioterapia y cálculo de fluidos.</Typography>
      <Grid container spacing={2}>
        <Grid item xs={6}><TextField fullWidth size="small" label="Peso (kg)" type="number" value={peso} onChange={(e) => setPeso(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }} /></Grid>
        <Grid item xs={6}><TextField fullWidth size="small" label="Altura (cm)" type="number" value={altura} onChange={(e) => setAltura(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }} /></Grid>
      </Grid>
      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
        <Button variant="contained" onClick={calcular} startIcon={<CalculateIcon />} sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none', background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)' }}>Calcular</Button>
        <Button variant="outlined" onClick={limpiar} startIcon={<RefreshIcon />} sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none', borderColor: '#e2e8f0', color: '#64748b' }}>Limpiar</Button>
      </Box>
      {resultado && <ResultadoBox titulo="Superficie Corporal" valor={resultado.valor} unidad="m²" color="#8b5cf6" />}
    </Box>
  );
};

// ===== DOSIS PEDIÁTRICAS =====
const CalculadoraDosisPediatrica = () => {
  const [pesoNino, setPesoNino] = useState('');
  const [dosisAdulto, setDosisAdulto] = useState('');
  const [resultado, setResultado] = useState(null);

  const calcular = () => {
    const peso = parseFloat(pesoNino), dosis = parseFloat(dosisAdulto);
    if (peso > 0 && dosis > 0) {
      const dosisPediatrica = (peso / 70) * dosis;
      setResultado({ valor: dosisPediatrica.toFixed(2) });
    }
  };

  const limpiar = () => { setPesoNino(''); setDosisAdulto(''); setResultado(null); };

  return (
    <Box>
      <Typography sx={{ fontSize: 12, color: '#64748b', mb: 2 }}>Calcula la dosis pediátrica basada en el peso del niño (Regla de Clark).</Typography>
      <Grid container spacing={2}>
        <Grid item xs={6}><TextField fullWidth size="small" label="Peso del niño (kg)" type="number" value={pesoNino} onChange={(e) => setPesoNino(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }} /></Grid>
        <Grid item xs={6}><TextField fullWidth size="small" label="Dosis adulto (mg)" type="number" value={dosisAdulto} onChange={(e) => setDosisAdulto(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }} /></Grid>
      </Grid>
      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
        <Button variant="contained" onClick={calcular} startIcon={<CalculateIcon />} sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none', background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)' }}>Calcular</Button>
        <Button variant="outlined" onClick={limpiar} startIcon={<RefreshIcon />} sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none', borderColor: '#e2e8f0', color: '#64748b' }}>Limpiar</Button>
      </Box>
      {resultado && <ResultadoBox titulo="Dosis Pediátrica Estimada" valor={resultado.valor} unidad="mg" color="#f59e0b" />}
      <Alert severity="warning" sx={{ mt: 2, fontSize: 11, borderRadius: 2 }}>Esta es una estimación. Siempre verificar con fuentes oficiales.</Alert>
    </Box>
  );
};

// ===== FECHA PROBABLE DE PARTO =====
const CalculadoraFPP = () => {
  const [fum, setFum] = useState('');
  const [resultado, setResultado] = useState(null);

  const calcular = () => {
    if (fum) {
      const fechaFUM = new Date(fum);
      const fpp = new Date(fechaFUM);
      fpp.setDate(fpp.getDate() + 7);
      fpp.setMonth(fpp.getMonth() - 3);
      fpp.setFullYear(fpp.getFullYear() + 1);
      const hoy = new Date();
      const diffDays = Math.floor((hoy - fechaFUM) / (1000 * 60 * 60 * 24));
      const semanas = Math.floor(diffDays / 7);
      const dias = diffDays % 7;
      const meses = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
      setResultado({
        fpp: `${fpp.getDate()} de ${meses[fpp.getMonth()]} de ${fpp.getFullYear()}`,
        semanas: `${semanas} semanas + ${dias} días`,
        trimestre: semanas < 13 ? 'Primer trimestre' : semanas < 27 ? 'Segundo trimestre' : 'Tercer trimestre'
      });
    }
  };

  const limpiar = () => { setFum(''); setResultado(null); };

  return (
    <Box>
      <Typography sx={{ fontSize: 12, color: '#64748b', mb: 2 }}>Calcula la Fecha Probable de Parto usando la Regla de Naegele.</Typography>
      <TextField fullWidth size="small" label="Fecha de Última Menstruación (FUM)" type="date" value={fum} onChange={(e) => setFum(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }} />
      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
        <Button variant="contained" onClick={calcular} startIcon={<CalculateIcon />} sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none', background: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)' }}>Calcular</Button>
        <Button variant="outlined" onClick={limpiar} startIcon={<RefreshIcon />} sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none', borderColor: '#e2e8f0', color: '#64748b' }}>Limpiar</Button>
      </Box>
      {resultado && (
        <>
          <ResultadoBox titulo="Fecha Probable de Parto" valor={resultado.fpp} color="#ec4899" />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 2 }}>
            <Box sx={{ p: 1.5, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0' }}>
              <InfoField label="Edad Gestacional" value={resultado.semanas} color="#a855f7" />
            </Box>
            <Box sx={{ p: 1.5, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0' }}>
              <InfoField label="Período" value={resultado.trimestre} color="#a855f7" />
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

// ===== ESCALA DE GLASGOW =====
const CalculadoraGlasgow = () => {
  const [ocular, setOcular] = useState(4);
  const [verbal, setVerbal] = useState(5);
  const [motor, setMotor] = useState(6);

  const total = ocular + verbal + motor;
  let interpretacion = '', color = '#10b981';
  if (total <= 8) { interpretacion = 'TCE grave'; color = '#dc2626'; }
  else if (total <= 12) { interpretacion = 'TCE moderado'; color = '#f59e0b'; }
  else { interpretacion = 'TCE leve'; color = '#10b981'; }

  const CriterioBox = ({ titulo, valor, setValor, opciones }) => (
    <Box sx={{ p: 1.5, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0', mb: 1.5 }}>
      <Typography sx={{ fontWeight: 600, fontSize: 12, mb: 1, color: '#1e3a5f' }}>{titulo}</Typography>
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {opciones.map(({ v, l }) => (
          <Chip key={v} label={l} size="small" onClick={() => setValor(v)}
            sx={{ bgcolor: valor === v ? '#1e3a5f' : '#f1f5f9', color: valor === v ? '#fff' : '#64748b', cursor: 'pointer', fontSize: 10, fontWeight: 600, '&:hover': { bgcolor: valor === v ? '#1e3a5f' : '#e2e8f0' } }} />
        ))}
      </Box>
    </Box>
  );

  return (
    <Box>
      <Typography sx={{ fontSize: 12, color: '#64748b', mb: 2 }}>Escala de Coma de Glasgow para evaluación del nivel de conciencia.</Typography>
      <CriterioBox titulo="Respuesta Ocular (E)" valor={ocular} setValor={setOcular} opciones={[{ v: 1, l: '1-Ninguna' }, { v: 2, l: '2-Al dolor' }, { v: 3, l: '3-Al habla' }, { v: 4, l: '4-Espontánea' }]} />
      <CriterioBox titulo="Respuesta Verbal (V)" valor={verbal} setValor={setVerbal} opciones={[{ v: 1, l: '1-Ninguna' }, { v: 2, l: '2-Sonidos' }, { v: 3, l: '3-Palabras' }, { v: 4, l: '4-Confusa' }, { v: 5, l: '5-Orientada' }]} />
      <CriterioBox titulo="Respuesta Motora (M)" valor={motor} setValor={setMotor} opciones={[{ v: 1, l: '1-Ninguna' }, { v: 2, l: '2-Extensión' }, { v: 3, l: '3-Flexión' }, { v: 4, l: '4-Retira' }, { v: 5, l: '5-Localiza' }, { v: 6, l: '6-Obedece' }]} />
      <ResultadoBox titulo={`Glasgow = E${ocular} + V${verbal} + M${motor}`} valor={total} unidad="/ 15" interpretacion={interpretacion} color={color} />
    </Box>
  );
};

// ===== APGAR =====
const CalculadoraAPGAR = () => {
  const [valores, setValores] = useState({ apariencia: 2, pulso: 2, gesticulacion: 2, actividad: 2, respiracion: 2 });
  const total = Object.values(valores).reduce((a, b) => a + b, 0);
  let interpretacion = '', color = '#10b981';
  if (total <= 3) { interpretacion = 'Depresión severa'; color = '#dc2626'; }
  else if (total <= 6) { interpretacion = 'Depresión moderada'; color = '#f59e0b'; }
  else { interpretacion = 'Normal'; color = '#10b981'; }

  const criterios = [
    { key: 'apariencia', nombre: 'Apariencia (color)', opciones: ['Azul/pálido', 'Cuerpo rosado', 'Rosado'] },
    { key: 'pulso', nombre: 'Pulso (FC)', opciones: ['Ausente', '<100 lpm', '>100 lpm'] },
    { key: 'gesticulacion', nombre: 'Gesticulación', opciones: ['Sin respuesta', 'Mueca', 'Llanto'] },
    { key: 'actividad', nombre: 'Actividad (tono)', opciones: ['Flácido', 'Flexión', 'Activo'] },
    { key: 'respiracion', nombre: 'Respiración', opciones: ['Ausente', 'Irregular', 'Vigorosa'] }
  ];

  return (
    <Box>
      <Typography sx={{ fontSize: 12, color: '#64748b', mb: 2 }}>Evaluación del recién nacido al minuto y a los 5 minutos de vida.</Typography>
      {criterios.map((c) => (
        <Box key={c.key} sx={{ p: 1.5, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0', mb: 1 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 11, mb: 0.5, color: '#1e3a5f' }}>{c.nombre}</Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {c.opciones.map((op, i) => (
              <Chip key={i} label={`${i} - ${op}`} size="small" onClick={() => setValores(prev => ({ ...prev, [c.key]: i }))}
                sx={{ bgcolor: valores[c.key] === i ? '#1e3a5f' : '#f1f5f9', color: valores[c.key] === i ? '#fff' : '#64748b', cursor: 'pointer', fontSize: 10, fontWeight: 600 }} />
            ))}
          </Box>
        </Box>
      ))}
      <ResultadoBox titulo="Puntuación APGAR" valor={total} unidad="/ 10" interpretacion={interpretacion} color={color} />
    </Box>
  );
};

// ===== WELLS TVP =====
const CalculadoraWells = () => {
  const [criterios, setCriterios] = useState({});
  const items = [
    { key: 'cancer', label: 'Cáncer activo', pts: 1 },
    { key: 'paralisis', label: 'Parálisis o inmovilización de MI', pts: 1 },
    { key: 'encamado', label: 'Encamado >3 días o cirugía reciente', pts: 1 },
    { key: 'dolor', label: 'Dolor en trayecto venoso profundo', pts: 1 },
    { key: 'edema', label: 'Edema de toda la extremidad', pts: 1 },
    { key: 'diferencia', label: 'Diferencia >3cm en pantorrilla', pts: 1 },
    { key: 'fovea', label: 'Edema con fóvea en pierna sintomática', pts: 1 },
    { key: 'colaterales', label: 'Venas superficiales colaterales', pts: 1 },
    { key: 'tvpPrevia', label: 'TVP documentada previamente', pts: 1 },
    { key: 'alternativo', label: 'Diagnóstico alternativo igual o más probable', pts: -2 }
  ];

  const puntaje = items.reduce((acc, item) => acc + (criterios[item.key] ? item.pts : 0), 0);
  let interpretacion = '', color = '#10b981', prob = '';
  if (puntaje <= 0) { interpretacion = 'Riesgo bajo'; color = '#10b981'; prob = '~5%'; }
  else if (puntaje <= 2) { interpretacion = 'Riesgo moderado'; color = '#f59e0b'; prob = '~17%'; }
  else { interpretacion = 'Riesgo alto'; color = '#ef4444'; prob = '~53%'; }

  return (
    <Box>
      <Typography sx={{ fontSize: 12, color: '#64748b', mb: 2 }}>Criterios de Wells para probabilidad de TVP.</Typography>
      {items.map((item) => (
        <Box key={item.key} onClick={() => setCriterios(prev => ({ ...prev, [item.key]: !prev[item.key] }))}
          sx={{ p: 1.5, mb: 1, borderRadius: 2, cursor: 'pointer', bgcolor: criterios[item.key] ? '#dbeafe' : '#fff', border: criterios[item.key] ? '1px solid #3b82f6' : '1px solid #e2e8f0', transition: 'all 0.15s', '&:hover': { bgcolor: criterios[item.key] ? '#dbeafe' : '#f8fafc' } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ fontSize: 12, color: criterios[item.key] ? '#1e40af' : '#64748b' }}>{item.label}</Typography>
            <Chip label={item.pts > 0 ? `+${item.pts}` : item.pts} size="small" sx={{ fontSize: 10, fontWeight: 700, bgcolor: item.pts < 0 ? '#dcfce7' : '#fef3c7', color: item.pts < 0 ? '#16a34a' : '#d97706' }} />
          </Box>
        </Box>
      ))}
      <ResultadoBox titulo="Score de Wells" valor={puntaje} unidad={`puntos (prob. ${prob})`} interpretacion={interpretacion} color={color} />
    </Box>
  );
};

// ===== RIESGO CARDIOVASCULAR =====
const CalculadoraRiesgoCV = () => {
  const [datos, setDatos] = useState({ edad: '', sexo: 'masculino', colesterol: '', hdl: '', presion: '', tabaco: false, diabetes: false });
  const [resultado, setResultado] = useState(null);

  const calcular = () => {
    const { edad, sexo, colesterol, hdl, presion, tabaco, diabetes } = datos;
    const e = parseFloat(edad), col = parseFloat(colesterol), hdlVal = parseFloat(hdl), pas = parseFloat(presion);
    if (e > 0 && col > 0 && hdlVal > 0 && pas > 0) {
      let puntos = 0;
      // Edad
      if (e < 35) puntos += 0; else if (e < 40) puntos += 2; else if (e < 45) puntos += 5;
      else if (e < 50) puntos += 6; else if (e < 55) puntos += 8; else if (e < 60) puntos += 10;
      else if (e < 65) puntos += 11; else puntos += 12;
      if (sexo === 'femenino') puntos -= 2;
      // Colesterol
      if (col < 160) puntos += 0; else if (col < 200) puntos += 1; else if (col < 240) puntos += 2; else puntos += 3;
      // HDL
      if (hdlVal >= 60) puntos -= 2; else if (hdlVal >= 50) puntos -= 1; else if (hdlVal < 40) puntos += 2;
      // Presión
      if (pas < 120) puntos += 0; else if (pas < 140) puntos += 1; else if (pas < 160) puntos += 2; else puntos += 3;
      // Factores
      if (tabaco) puntos += 4;
      if (diabetes) puntos += 3;

      let riesgo = '', interpretacion = '', color = '#10b981';
      if (puntos < 5) { riesgo = '<5%'; interpretacion = 'Riesgo bajo'; color = '#10b981'; }
      else if (puntos < 10) { riesgo = '5-10%'; interpretacion = 'Riesgo moderado'; color = '#f59e0b'; }
      else if (puntos < 15) { riesgo = '10-20%'; interpretacion = 'Riesgo alto'; color = '#ef4444'; }
      else { riesgo = '>20%'; interpretacion = 'Riesgo muy alto'; color = '#dc2626'; }
      setResultado({ puntos, riesgo, interpretacion, color });
    }
  };

  const limpiar = () => { setDatos({ edad: '', sexo: 'masculino', colesterol: '', hdl: '', presion: '', tabaco: false, diabetes: false }); setResultado(null); };

  return (
    <Box>
      <Typography sx={{ fontSize: 12, color: '#64748b', mb: 2 }}>Estimación de riesgo cardiovascular a 10 años (Framingham simplificado).</Typography>
      <Grid container spacing={2}>
        <Grid item xs={6}><TextField fullWidth size="small" label="Edad (años)" type="number" value={datos.edad} onChange={(e) => setDatos(d => ({ ...d, edad: e.target.value }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }} /></Grid>
        <Grid item xs={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Sexo</InputLabel>
            <Select value={datos.sexo} label="Sexo" onChange={(e) => setDatos(d => ({ ...d, sexo: e.target.value }))} sx={{ borderRadius: 2, bgcolor: '#fff' }}>
              <MenuItem value="masculino">Masculino</MenuItem>
              <MenuItem value="femenino">Femenino</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={4}><TextField fullWidth size="small" label="Colesterol (mg/dL)" type="number" value={datos.colesterol} onChange={(e) => setDatos(d => ({ ...d, colesterol: e.target.value }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }} /></Grid>
        <Grid item xs={4}><TextField fullWidth size="small" label="HDL (mg/dL)" type="number" value={datos.hdl} onChange={(e) => setDatos(d => ({ ...d, hdl: e.target.value }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }} /></Grid>
        <Grid item xs={4}><TextField fullWidth size="small" label="PAS (mmHg)" type="number" value={datos.presion} onChange={(e) => setDatos(d => ({ ...d, presion: e.target.value }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }} /></Grid>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip label="Fumador" onClick={() => setDatos(d => ({ ...d, tabaco: !d.tabaco }))} sx={{ bgcolor: datos.tabaco ? '#ef4444' : '#f1f5f9', color: datos.tabaco ? '#fff' : '#64748b', cursor: 'pointer', fontWeight: 600 }} />
            <Chip label="Diabetes" onClick={() => setDatos(d => ({ ...d, diabetes: !d.diabetes }))} sx={{ bgcolor: datos.diabetes ? '#f59e0b' : '#f1f5f9', color: datos.diabetes ? '#fff' : '#64748b', cursor: 'pointer', fontWeight: 600 }} />
          </Box>
        </Grid>
      </Grid>
      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
        <Button variant="contained" onClick={calcular} startIcon={<CalculateIcon />} sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none', background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)' }}>Calcular</Button>
        <Button variant="outlined" onClick={limpiar} startIcon={<RefreshIcon />} sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none', borderColor: '#e2e8f0', color: '#64748b' }}>Limpiar</Button>
      </Box>
      {resultado && <ResultadoBox titulo={`Riesgo CV a 10 años (${resultado.puntos} pts)`} valor={resultado.riesgo} interpretacion={resultado.interpretacion} color={resultado.color} />}
    </Box>
  );
};

// Mapeo de calculadoras
const CALCULADORAS_COMPONENTES = {
  imc: <CalculadoraIMC />,
  creatinina: <CalculadoraClearance />,
  superficie: <CalculadoraSuperficie />,
  pediatrica: <CalculadoraDosisPediatrica />,
  fpp: <CalculadoraFPP />,
  glasgow: <CalculadoraGlasgow />,
  apgar: <CalculadoraAPGAR />,
  wells: <CalculadoraWells />,
  riesgocv: <CalculadoraRiesgoCV />
};

// ===== COMPONENTE PRINCIPAL =====
const CalculadorasMedicas = ({ open, onClose }) => {
  const [calcActual, setCalcActual] = useState('imc');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: "#f8fafc",
          boxShadow: "0 8px 32px rgba(99, 102, 241, 0.15)",
          border: "none",
          overflow: "hidden"
        }
      }}
    >
      {/* Header - igual que CargarFlujoDialog */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)",
          color: "#fff",
          py: 2,
          px: 3
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <CalculateIcon sx={{ fontSize: 26, color: "#fbbf24" }} />
          <Typography sx={{ fontWeight: 700, fontSize: 20, letterSpacing: 0.5 }}>
            Calculadoras Médicas
          </Typography>
          <Chip
            label="9 herramientas"
            size="small"
            sx={{
              ml: 1,
              background: "rgba(255,255,255,0.2)",
              color: "#fff",
              fontWeight: 600,
              fontSize: 11
            }}
          />
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Selector de calculadoras - estilo tarjetas */}
        <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #e2e8f0", background: "#fff" }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {CALCULADORAS.map((calc) => (
              <Chip
                key={calc.id}
                icon={calc.icon}
                label={calc.name}
                onClick={() => setCalcActual(calc.id)}
                sx={{
                  bgcolor: calcActual === calc.id ? calc.color : '#f1f5f9',
                  color: calcActual === calc.id ? '#fff' : '#64748b',
                  fontWeight: 600,
                  fontSize: 12,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '& .MuiChip-icon': { color: calcActual === calc.id ? '#fff' : calc.color },
                  '&:hover': { bgcolor: calcActual === calc.id ? calc.color : '#e2e8f0' }
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Contenido de la calculadora */}
        <Box sx={{ p: 3, maxHeight: 450, overflowY: 'auto' }}>
          {CALCULADORAS_COMPONENTES[calcActual]}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e2e8f0", background: "#fff" }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: 2,
            fontWeight: 600,
            px: 3,
            textTransform: 'none',
            borderColor: "#e2e8f0",
            color: "#64748b",
            "&:hover": { background: "#f1f5f9", borderColor: "#94a3b8" }
          }}
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CalculadorasMedicas;
